"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BasicEnumerable = void 0;
const aggregate_1 = require("../functions/aggregate");
const all_1 = require("../functions/all");
const any_1 = require("../functions/any");
const applyAppend_1 = require("../functions/applicators/applyAppend");
const applyAsEnumerable_1 = require("../functions/applicators/applyAsEnumerable");
const applyChunk_1 = require("../functions/applicators/applyChunk");
const applyConcat_1 = require("../functions/applicators/applyConcat");
const applyDefaultIfEmpty_1 = require("../functions/applicators/applyDefaultIfEmpty");
const applyDistinct_1 = require("../functions/applicators/applyDistinct");
const applyExcept_1 = require("../functions/applicators/applyExcept");
const applyFlatten_1 = require("../functions/applicators/applyFlatten");
const applyFullJoin_1 = require("../functions/applicators/applyFullJoin");
const applyGroupBy_1 = require("../functions/applicators/applyGroupBy");
const applyGroupJoin_1 = require("../functions/applicators/applyGroupJoin");
const applyIntersect_1 = require("../functions/applicators/applyIntersect");
const applyJoin_1 = require("../functions/applicators/applyJoin");
const applyLeftJoin_1 = require("../functions/applicators/applyLeftJoin");
const applyMax_1 = require("../functions/applicators/applyMax");
const applyMin_1 = require("../functions/applicators/applyMin");
const applyOfType_1 = require("../functions/applicators/applyOfType");
const applyOrderBy_1 = require("../functions/applicators/applyOrderBy");
const applyPipe_1 = require("../functions/applicators/applyPipe");
const applyPrepend_1 = require("../functions/applicators/applyPrepend");
const applyQuantile_1 = require("../functions/applicators/applyQuantile");
const applyReverse_1 = require("../functions/applicators/applyReverse");
const applyRightJoin_1 = require("../functions/applicators/applyRightJoin");
const applySelect_1 = require("../functions/applicators/applySelect");
const applyShuffle_1 = require("../functions/applicators/applyShuffle");
const applySkip_1 = require("../functions/applicators/applySkip");
const applySplit_1 = require("../functions/applicators/applySplit");
const applyTake_1 = require("../functions/applicators/applyTake");
const applyUnion_1 = require("../functions/applicators/applyUnion");
const applyWhere_1 = require("../functions/applicators/applyWhere");
const applyWindow_1 = require("../functions/applicators/applyWindow");
const applyXor_1 = require("../functions/applicators/applyXor");
const applyZip_1 = require("../functions/applicators/applyZip");
const atLeast_1 = require("../functions/atLeast");
const atMost_1 = require("../functions/atMost");
const average_1 = require("../functions/average");
const contains_1 = require("../functions/contains");
const count_1 = require("../functions/count");
const elementAt_1 = require("../functions/elementAt");
const endsWith_1 = require("../functions/endsWith");
const first_1 = require("../functions/first");
const forEach_1 = require("../functions/forEach");
const last_1 = require("../functions/last");
const sequenceEqual_1 = require("../functions/sequenceEqual");
const single_1 = require("../functions/single");
const startsWith_1 = require("../functions/startsWith");
const sum_1 = require("../functions/sum");
const to_1 = require("../functions/to");
const toArray_1 = require("../functions/toArray");
const toMap_1 = require("../functions/toMap");
const toObject_1 = require("../functions/toObject");
const toSet_1 = require("../functions/toSet");
/**
 * Class that exposes an iterator, which supports a simple iteration and various methods.
 * @typeparam TSource The type of elements in the Enumerable.
 */
class BasicEnumerable {
    constructor(factory, srcGenerator) {
        this.factory = factory;
        this.srcGenerator = srcGenerator;
    }
    /**
     * Exposes an iterator that can be used for iteration.
     * @example
     * ```typescript
     * const numbers = from([1, 2, 3]);
     * for (const number of numbers) {
     *   // Iteration is allowed because of the iterator.
     * }
     * ```
     * @returns The iterator for the IEnumerable.
     */
    [Symbol.iterator]() {
        return this.srcGenerator();
    }
    aggregate(seedOrAggregator, aggregator, resultSelector) {
        return (0, aggregate_1.aggregate)(this, seedOrAggregator, aggregator, resultSelector);
    }
    /**
     * Determines whether all elements of a sequence satisfy a condition.
     * @example
     * ```typescript
     * const numbers = [1, 2, 3, 4];
     * const areAllNumbersEven = from(numbers).all(x => x % 2 === 0); // false
     * ```
     * @param predicate A function to test each element for a condition.
     * @returns true if every element of the source sequence passes the test in the specified predicate, or if the sequence is empty; otherwise, false.
     */
    all(predicate) {
        return (0, all_1.all)(this, predicate);
    }
    any(predicate) {
        return (0, any_1.any)(this, predicate);
    }
    /**
     * Appends a value to the end of the sequence.
     * @example
     * ```typescript
     * const numbers = [1, 2, 3, 4];
     * const withFive = from(numbers).append(5); // [1, 2, 3, 4, 5]
     * ```
     * @param item The value to append.
     * @returns A new sequence that ends with element.
     */
    append(item) {
        return (0, applyAppend_1.applyAppend)(this.factory, this, item);
    }
    /**
     * Returns the input as an IEnumerable.
     * @example
     * ```typescript
     * const items = [1, 2, 3];
     * const asEnumerable = from(items).asEnumerable();
     * ```
     * @returns The input sequence as IEnumerable.
     */
    asEnumerable() {
        return (0, applyAsEnumerable_1.applyAsEnumerable)(this.factory, this);
    }
    atLeast(count, predicate) {
        return (0, atLeast_1.atLeast)(this, count, predicate);
    }
    atMost(count, predicate) {
        return (0, atMost_1.atMost)(this, count, predicate);
    }
    average(selector) {
        return (0, average_1.average)(this, selector);
    }
    /**
     * Split the elements of a sequence into chunks of size at most chunkSize.
     * @example
     * ```typescript
     * const numbers = [1, 2, 3, 4, 5];
     * const chunks = from(numbers).chunk(2); // [[1, 2], [3, 4], [5]]
     * ```
     * @param chunkSize The maximum size of each chunk.
     * @returns An IEnumerable<TSource> that contains the elements the input sequence split into chunks of size chunkSize.
     */
    chunk(chunkSize) {
        return (0, applyChunk_1.applyChunk)(this.factory, this, chunkSize);
    }
    concat(...collections) {
        return (0, applyConcat_1.applyConcat)(this.factory, this, collections);
    }
    contains(value, equalityComparer) {
        return (0, contains_1.contains)(this, value, equalityComparer);
    }
    count(predicate) {
        return (0, count_1.count)(this, predicate);
    }
    /**
     * Returns the elements of the specified sequence or the specified value in a singleton collection if the sequence is empty.
     * @example
     * ```typescript
     * const defaultNum = 0;
     * const items = [];
     * const itemsWithDefault = from(items).defaultIfEmpty(defaultNum); // [0];
     * ```
     * @param defaultItem The value to return if the sequence is empty.
     * @returns An IEnumerable<TSource> that contains defaultValue if source is empty; otherwise, source.
     */
    defaultIfEmpty(defaultItem) {
        return (0, applyDefaultIfEmpty_1.applyDefaultIfEmpty)(this.factory, this, defaultItem);
    }
    distinct(equalityComparer) {
        return (0, applyDistinct_1.applyDistinct)(this.factory, this, x => x, equalityComparer);
    }
    distinctBy(keySelector, equalityComparer) {
        return (0, applyDistinct_1.applyDistinct)(this.factory, this, keySelector, equalityComparer);
    }
    /**
     * Returns the element at a specified index in a sequence or throws if the index is out of range.
     * A negative index can be used to get element starting from the end.
     * @example
     * ```typescript
     * const items = [1, 2, 3];
     * const indexZero = from(items).elementAt(0); // Will be 1
     * const willBeNull = from(items).elementAt(10); // Will throw.
     * const last = from(items).elementAt(-1); // 3
     * ```
     * @param index The zero-based index of the element to retrieve.
     * @returns The element at the specified position in the source sequence.
     */
    elementAt(index) {
        return (0, elementAt_1.elementAt)(this, index);
    }
    /**
     * Returns the element at a specified index in a sequence or null if the index is out of range.
     * A negative index can be used to get element starting from the end.
     * @example
     * ```typescript
     * const items = [1, 2, 3];
     * const indexZero = from(items).elementAtOrDefault(0); // Will be 1
     * const willBeNull = from(items).elementAtOrDefault(10); // Will be null.
     * const last = from(items).elementAtOrDefault(-1); // 3
     * ```
     * @param index The zero-based index of the element to retrieve.
     * @returns The element at the specified position in the source sequence.
     */
    elementAtOrDefault(index) {
        return (0, elementAt_1.elementAtOrDefault)(this, index);
    }
    endsWith(second, equalityComparer) {
        return (0, endsWith_1.endsWith)(this, second, equalityComparer);
    }
    except(...second) {
        return (0, applyExcept_1.applyExcept)(this.factory, x => x, this, second);
    }
    exceptBy(...second) {
        return (0, applyExcept_1.applyExcept)(this.factory, null, this, second);
    }
    first(condition) {
        return (0, first_1.first)(this, condition);
    }
    firstOrDefault(condition) {
        return (0, first_1.firstOrDefault)(this, condition);
    }
    flatten(depth) {
        return (0, applyFlatten_1.applyFlatten)(this.factory, this, depth);
    }
    /**
     * Iterates the sequence and calls an action on each element.
     * @param action The action to perform on each item in the sequence.
     */
    forEach(callback) {
        (0, forEach_1.forEach)(this, callback);
    }
    fullJoinHeterogeneous(second, firstKeySelector, secondKeySelector, firstSelector, secondSelector, bothSelector, equalityComparer) {
        return (0, applyFullJoin_1.applyFullJoinHeterogeneous)(this.factory, this, second, firstKeySelector, secondKeySelector, firstSelector, secondSelector, bothSelector, equalityComparer);
    }
    fullJoinHomogeneous(second, keySelector, firstSelector, secondSelector, bothSelector, equalityComparer) {
        return (0, applyFullJoin_1.applyFullJoinHomogeneous)(this.factory, this, second, keySelector, firstSelector, secondSelector, bothSelector, equalityComparer);
    }
    groupBy(keySelector, equalityComparer) {
        return (0, applyGroupBy_1.applyGroupBy)(this.factory, this, keySelector, equalityComparer);
    }
    groupJoin(inner, outerKeySelector, innerKeySelector, resultSelector, equalityComparer) {
        return (0, applyGroupJoin_1.applyGroupJoin)(this.factory, this, inner, outerKeySelector, innerKeySelector, resultSelector, equalityComparer);
    }
    intersect(...second) {
        return (0, applyIntersect_1.applyIntersect)(this.factory, x => x, this, second);
    }
    intersectBy(...second) {
        return (0, applyIntersect_1.applyIntersect)(this.factory, null, this, second);
    }
    join(inner, outerKeySelector, innerKeySelector, resultSelector, equalityComparer) {
        return (0, applyJoin_1.applyJoin)(this.factory, this, inner, outerKeySelector, innerKeySelector, resultSelector, equalityComparer);
    }
    last(predicate) {
        return (0, last_1.last)(this, predicate);
    }
    lastOrDefault(predicate) {
        return (0, last_1.lastOrDefault)(this, predicate);
    }
    leftJoinHeterogeneous(second, firstKeySelector, secondKeySelector, firstSelector, bothSelector, equalityComparer) {
        return (0, applyLeftJoin_1.applyLeftJoinHeterogeneous)(this.factory, this, second, firstKeySelector, secondKeySelector, firstSelector, bothSelector, equalityComparer);
    }
    leftJoinHomogeneous(second, keySelector, firstSelector, bothSelector, equalityComparer) {
        return (0, applyLeftJoin_1.applyLeftJoinHomogeneous)(this.factory, this, second, keySelector, firstSelector, bothSelector, equalityComparer);
    }
    max(selector) {
        return (0, applyMax_1.applyMax)(this.factory, this, x => x, selector);
    }
    /**
     * Returns the maximum value in a generic sequence according to a specified key selector function.
     * @typeparam TKey The type of key to compare elements by.
     * @param keySelector A function to extract the key for each element.
     * @returns The value with the maximum key in the sequence.
     */
    maxBy(keySelector) {
        return (0, applyMax_1.applyMax)(this.factory, this, keySelector);
    }
    min(selector) {
        return (0, applyMin_1.applyMin)(this.factory, this, x => x, selector);
    }
    /**
     * Returns the min value in a generic sequence according to a specified key selector function.
     * @typeparam TKey The type of key to compare elements by.
     * @param keySelector A function to extract the key for each element.
     * @returns The value with the min key in the sequence.
     */
    minBy(keySelector) {
        return (0, applyMin_1.applyMin)(this.factory, this, keySelector);
    }
    /**
     * Filters the elements of an IEnumerable based on a specified type.
     * @typeparam TResult The type to filter the elements of the sequence on.
     * @param type The type to filter the elements of the sequence on.
     * @returns An IEnumerable<T> that contains elements from the input sequence of type TResult.
     */
    ofType(type) {
        return (0, applyOfType_1.applyOfType)(this.factory, this, type);
    }
    orderBy(selector, comparer) {
        return (0, applyOrderBy_1.applyOrderBy)(this.factory, this, true, selector, comparer);
    }
    orderByDescending(keySelector, comparer) {
        return (0, applyOrderBy_1.applyOrderBy)(this.factory, this, false, keySelector, comparer);
    }
    /**
     * Executes the given action on each element in the source sequence and yields it.
     * @param action The action to execute on each element.
     * @returns A sequence with source elements in their original order.
     */
    pipe(action) {
        return (0, applyPipe_1.applyPipe)(this.factory, this, action);
    }
    /**
     * Adds a value to the beginning of the sequence.
     * @param item The value to prepend to source.
     * @returns A new sequence that begins with item.
     */
    prepend(item) {
        return (0, applyPrepend_1.applyPrepend)(this.factory, this, item);
    }
    /**
     * Computes the quantile of a sequence.
     * @param selector A function to extract a value from each element.
     * @param q The percentile to compute (25, 50, etc.)
     * @returns The percentile of the sequence.
     */
    quantile(selector, q) {
        return (0, applyQuantile_1.applyQuantile)(this.factory, this, selector, q);
    }
    /**
     * Inverts the order of the elements in a sequence.
     * @returns A sequence whose elements correspond to those of the input sequence in reverse order.
     */
    reverse() {
        return (0, applyReverse_1.applyReverse)(this.factory, this);
    }
    rightJoinHeterogeneous(second, firstKeySelector, secondKeySelector, secondSelector, bothSelector, equalityComparer) {
        return (0, applyRightJoin_1.applyRightJoinHeterogeneous)(this.factory, this, second, firstKeySelector, secondKeySelector, secondSelector, bothSelector, equalityComparer);
    }
    rightJoinHomogeneous(second, keySelector, secondSelector, bothSelector, equalityComparer) {
        return (0, applyRightJoin_1.applyRightJoinHomogeneous)(this.factory, this, second, keySelector, secondSelector, bothSelector, equalityComparer);
    }
    /**
     * Projects each element of a sequence into a new form.
     * @typeparam TResult The type of the value returned by selector.
     * @param selector A transform function to apply to each element.
     * @returns An IEnumerable<T> whose elements are the result of invoking the transform function on each element of source.
     */
    select(selector) {
        return (0, applySelect_1.applySelect)(this.factory, this, selector);
    }
    /**
     * Projects each element of a sequence to an IEnumerable<T> and flattens the resulting sequences into one sequence.
     * @typeparam TResult The type of the value returned by selector.
     * @param selector A transform function to apply to each source element; the second parameter of the function represents the index of the source element.
     * @returns An IEnumerable<T> whose elements are the result of invoking the one-to-many transform function on each element of an input sequence.
     */
    selectMany(selector) {
        return (0, applySelect_1.applySelectMany)(this.factory, this, selector);
    }
    sequenceEqual(second, equalityComparer) {
        return (0, sequenceEqual_1.sequenceEqual)(this, second, equalityComparer);
    }
    /**
     * Returns a new IEnumerable<TSource> of the input sequence in random order.
     * @returns A new IEnumerable<TSource> of the input sequence in random order.
     */
    shuffle() {
        return (0, applyShuffle_1.applyShuffle)(this.factory, this);
    }
    single(predicate) {
        return (0, single_1.single)(this, predicate);
    }
    singleOrDefault(predicate) {
        return (0, single_1.singleOrDefault)(this, predicate);
    }
    /**
     * Bypasses a specified number of elements in a sequence and then returns the remaining elements.
     * @param count The number of elements to skip before returning the remaining elements.
     * @returns An IEnumerable<T> that contains the elements that occur after the specified index in the input sequence.
     */
    skip(count) {
        return (0, applySkip_1.applySkip)(this.factory, this, count);
    }
    /**
     * Returns a new enumerable collection that contains the elements from source with the last count elements of the source collection omitted.
     * @param count The number of elements to omit from the end of the collection.
     * @returns A new enumerable collection that contains the elements from source minus count elements from the end of the collection.
     */
    skipLast(count) {
        return (0, applySkip_1.applySkipLast)(this.factory, this, count);
    }
    /**
     * Bypasses elements in a sequence as long as a specified condition is true and then returns the remaining elements.
     * @param predicate A function to test each source element for a condition; the second parameter of the function represents the index of the source element.
     * @returns An IEnumerable<T> that contains the elements from the input sequence starting at the first element in the linear series that does not pass the test specified by predicate.
     */
    skipWhile(predicate) {
        return (0, applySkip_1.applySkipWhile)(this.factory, this, predicate);
    }
    split(separatorOrPredicate) {
        return (0, applySplit_1.applySplit)(this.factory, this, separatorOrPredicate);
    }
    startsWith(second, equalityComparer) {
        return (0, startsWith_1.startsWith)(this, second, equalityComparer);
    }
    sum(selector) {
        return (0, sum_1.sum)(this, selector);
    }
    /**
     * Returns a specified number of contiguous elements from the start of a sequence.
     * @returns An IEnumerable<T> that contains the specified number of elements from the start of the input sequence.
     */
    take(count) {
        return (0, applyTake_1.applyTake)(this.factory, this, count);
    }
    /**
     * Returns every N-th element of a sequence.
     * @param step Number of elements to bypass before returning the next element.
     * @returns A sequence with every N-th element of the input sequence.
     */
    takeEvery(step) {
        return (0, applyTake_1.applyTakeEvery)(this.factory, this, step);
    }
    /**
     * Returns a new enumerable collection that contains the last count elements from source.
     * @param count The number of elements to take from the end of the collection.
     * @returns A new enumerable collection that contains the last count elements from source.
     */
    takeLast(count) {
        return (0, applyTake_1.applyTakeLast)(this.factory, this, count);
    }
    /**
     * Returns elements from a sequence as long as a specified condition is true, and then skips the remaining elements.
     * @param predicate A function to test each source element for a condition; the second parameter of the function represents the index of the source element.
     * @returns An IEnumerable<T> that contains elements from the input sequence that occur before the element at which the test no longer passes.
     */
    takeWhile(predicate) {
        return (0, applyTake_1.applyTakeWhile)(this.factory, this, predicate);
    }
    /**
     * Creates a new instance of the passed in ctor with the Enumerable as input.
     * Useful for custom iterables.
     * @example
     * ```typescript
     * class MyCustomEnumerable<T> extends Enumerable<T> {
     *   public foo(): void {
     *     console.log('hello');
     *   }
     * }
     *
     * const items = [1, 2, 3];
     *
     * const asCustom = from(items).to(MyCustomEnumerable); // asCustom is now a MyCustomEnumerable instance.
     * ```
     * @typeparam TResult The type of the returned object.
     * @param ctor The constructor function to create the result.
     * @returns A new instance of the passed in ctor with the enumerable passed as input.
     */
    to(ctor) {
        return (0, to_1.to)(this, ctor);
    }
    /**
     * Converts the source sequence into an array.
     * @returns A new array containing the elements of the source sequence.
     */
    toArray() {
        return (0, toArray_1.toArray)(this);
    }
    /**
     * Returns a JSON string representation of the enumerable.
     * @returns A JSON string representation of the enumerable.
     */
    toJSON() {
        return JSON.stringify(this.toArray());
    }
    /**
     * Converts the source sequence into list.
     * @returns A new list containing the elements of the source sequence.
     */
    toList() {
        return this.factory.createList(this.srcGenerator);
    }
    /**
     * @deprecated Not yet implemented. Do not use.
     */
    toLookup() {
        throw new Error('Not yet implemented');
    }
    toMap(keySelector, valueSelector) {
        return (0, toMap_1.toMap)(this, keySelector, valueSelector);
    }
    toObject(keySelector, valueSelector) {
        return (0, toObject_1.toObject)(this, keySelector, valueSelector);
    }
    /**
     * Creates a Set<T> from an IEnumerable<T>.
     * @returns A Set<T> that contains values of type TSource selected from the input sequence.
     */
    toSet() {
        return (0, toSet_1.toSet)(this);
    }
    /**
     * Returns a string representation of the enumerable.
     * @returns A string representation of the enumerable.
     */
    toString() {
        return this.toArray().toString();
    }
    union(...second) {
        return (0, applyUnion_1.applyUnion)(this.factory, x => x, this, second);
    }
    unionBy(...second) {
        return (0, applyUnion_1.applyUnion)(this.factory, null, this, second);
    }
    /**
     * Filters a sequence of values based on a predicate.
     * @example
     * ```typescript
     * const items = [1, 2, 3, 4, 5];
     * const greaterThanTwo = from(items).where(x => x > 2); // Will be [3, 4, 5]
     * ```
     * @param predicate A function to test each source element for a condition; the second parameter of the function represents the index of the source element.
     * @returns An IEnumerable<TSource> that contains elements from the input sequence that satisfy the condition.
     */
    where(predicate) {
        return (0, applyWhere_1.applyWhere)(this.factory, this, predicate);
    }
    /**
     * Processes a sequence into a series of subsequences representing a windowed subset of the original.
     * If size is greater than source.length, no subsequences will be returned.
     * @param size The size (number of elements) in each window.
     * @returns A series of sequences representing each sliding window subsequence.
     */
    window(size) {
        return (0, applyWindow_1.applyWindow)(this.factory, this, size);
    }
    xor(...second) {
        return (0, applyXor_1.applyXor)(this.factory, x => x, this, second);
    }
    xorBy(...second) {
        return (0, applyXor_1.applyXor)(this.factory, null, this, second);
    }
    zip(second, resultSelector) {
        return (0, applyZip_1.applyZip)(this.factory, this, second, resultSelector);
    }
}
exports.BasicEnumerable = BasicEnumerable;
//# sourceMappingURL=BasicEnumerable.js.map